/**
 * WhatsApp Chat Meta Store
 * Gestisce: stato conversazione, operatore assegnato, labels, note interne, template messaggi
 */

import { create } from 'zustand';
import { supabase } from '../supabase';
import { syncChatLabelToWhatsApp, type LabelSyncResult } from '../services/whatsapp/wahaLabels';

// ============ TYPES ============

export interface ChatLabel {
  id: string;
  organization_id: string;
  name: string;
  color: string;
  created_at: string;
  // Etichetta collegata a WhatsApp Business (migration 20260926g); assenti = solo CRM.
  config_id?: string | null;
  waha_label_id?: string | null;
  origin?: 'crm' | 'whatsapp' | null;
}

export interface ChatMeta {
  chat_jid: string;
  conversation_status: 'da_gestire' | 'in_gestione' | 'chiusa';
  assigned_operator_id: string | null;
  assigned_at: string | null;
  // Preferenze della lista (migration 20260926a): facoltative finche' non e' applicata.
  is_pinned?: boolean;
  is_archived?: boolean;
  archived_at?: string | null;
  muted_until?: string | null;
}

/** Fissa in alto / Archivia / Silenzia: le colonne che l'utente cambia dal menu della chat. */
export type ChatListPrefsChange = Partial<Pick<ChatMeta, 'is_pinned' | 'is_archived' | 'archived_at' | 'muted_until'>>;

export interface ChatNote {
  id: string;
  chat_jid: string;
  content: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  author_name?: string;
}

export interface MessageTemplate {
  id: string;
  organization_id: string;
  name: string;
  content: string;
  category: string | null;
  created_at: string;
}

export interface OrgMember {
  id: string;
  full_name: string;
  email: string;
  profile_image_url: string | null;
}

export interface ChatFilters {
  status: 'da_gestire' | 'in_gestione' | 'chiusa' | null;
  labelId: string | null;
  myChatsOnly: boolean;
  myProjectsOnly: boolean;
  unreadOnly: boolean;
  groupsOnly: boolean;
  /** Solo le chat col promemoria "Ricontatta" scaduto. */
  recontactOnly: boolean;
}

// ============ STATE ============

interface WhatsAppChatMetaState {
  // Labels
  orgLabels: ChatLabel[];
  chatLabels: Map<string, ChatLabel[]>; // chat_jid -> labels

  // Meta (stato + operatore)
  chatMeta: Map<string, ChatMeta>; // chat_jid -> meta

  // Note interne
  chatNotes: Map<string, ChatNote[]>; // chat_jid -> notes
  notesLoading: boolean;

  // Template
  templates: MessageTemplate[];
  templatesLoading: boolean;

  // Team members
  orgMembers: OrgMember[];

  // Filtri attivi
  filters: ChatFilters;

  /** false se le colonne di fissa/archivia/silenzia non ci sono ancora nel DB (migration 20260926a da applicare). */
  listPrefsAvailable: boolean;
}

interface WhatsAppChatMetaActions {
  // Labels
  fetchOrgLabels: (organizationId: string) => Promise<void>;
  fetchChatLabels: (chatJids: string[], organizationId: string) => Promise<void>;
  createLabel: (organizationId: string, name: string, color: string) => Promise<ChatLabel | null>;
  deleteLabel: (labelId: string) => Promise<void>;
  /** Salva nel CRM e, se l'etichetta e' collegata a WhatsApp, la riporta sul telefono. */
  /** chatConfigId = numero della chat aperta: si scrive sul telefono solo per le etichette collegate a quel numero. */
  assignLabel: (chatJid: string, labelId: string, organizationId: string, chatConfigId?: string | null) => Promise<LabelSyncResult>;
  removeLabel: (chatJid: string, labelId: string, organizationId: string, chatConfigId?: string | null) => Promise<LabelSyncResult>;

  // Meta
  fetchChatMeta: (chatJids: string[], organizationId: string) => Promise<void>;
  upsertMeta: (chatJid: string, changes: Partial<Omit<ChatMeta, 'chat_jid'>>, organizationId: string) => Promise<void>;
  /** Solo stato locale: la riga DB la riapre il trigger sul messaggio in arrivo. */
  reopenIfClosed: (chatJid: string) => void;
  /** Fissa in alto, archivia, silenzia (ottimistico; torna false se il salvataggio fallisce). */
  setChatListPrefs: (chatJid: string, changes: ChatListPrefsChange, organizationId: string) => Promise<boolean>;

  // Note
  fetchNotes: (chatJid: string, organizationId: string) => Promise<void>;
  addNote: (chatJid: string, content: string, organizationId: string) => Promise<void>;
  deleteNote: (noteId: string, chatJid: string) => Promise<void>;

  // Template
  fetchTemplates: (organizationId: string) => Promise<void>;
  createTemplate: (organizationId: string, name: string, content: string, category?: string) => Promise<void>;
  deleteTemplate: (templateId: string) => Promise<void>;

  // Team
  fetchOrgMembers: (organizationId: string) => Promise<void>;

  // Filtri
  setFilters: (filters: Partial<ChatFilters>) => void;
  resetFilters: () => void;
}

type WhatsAppChatMetaStore = WhatsAppChatMetaState & WhatsAppChatMetaActions;

const DEFAULT_FILTERS: ChatFilters = {
  status: null,
  labelId: null,
  myChatsOnly: false,
  myProjectsOnly: false,
  unreadOnly: false,
  groupsOnly: false,
  recontactOnly: false,
};

const META_BASE_COLUMNS = 'chat_jid, conversation_status, assigned_operator_id, assigned_at';
const META_PREFS_COLUMNS = `${META_BASE_COLUMNS}, is_pinned, is_archived, archived_at, muted_until`;

// ============ STORE ============

export const useWhatsAppChatMetaStore = create<WhatsAppChatMetaStore>((set, get) => ({
  orgLabels: [],
  chatLabels: new Map(),
  chatMeta: new Map(),
  chatNotes: new Map(),
  notesLoading: false,
  templates: [],
  templatesLoading: false,
  orgMembers: [],
  filters: { ...DEFAULT_FILTERS },
  listPrefsAvailable: true,

  // ============ LABELS ============

  fetchOrgLabels: async (organizationId) => {
    const { data, error } = await supabase
      .from('whatsapp_chat_labels')
      .select('*')
      .eq('organization_id', organizationId)
      .order('name');
    if (!error && data) set({ orgLabels: data });
  },

  fetchChatLabels: async (chatJids, organizationId) => {
    if (chatJids.length === 0) return;
    const { data, error } = await supabase
      .from('whatsapp_chat_label_assignments')
      .select('chat_jid, label:label_id(*)')
      .eq('organization_id', organizationId)
      .in('chat_jid', chatJids);
    if (error || !data) return;

    const newMap = new Map(get().chatLabels);
    // Reset chat_jids che stiamo aggiornando
    for (const jid of chatJids) newMap.set(jid, []);
    for (const row of data as any[]) {
      if (row.label) {
        const existing = newMap.get(row.chat_jid) || [];
        newMap.set(row.chat_jid, [...existing, row.label]);
      }
    }
    set({ chatLabels: newMap });
  },

  createLabel: async (organizationId, name, color) => {
    const { data, error } = await supabase
      .from('whatsapp_chat_labels')
      .insert({ organization_id: organizationId, name, color })
      .select()
      .single();
    if (error || !data) return null;
    set((s) => ({ orgLabels: [...s.orgLabels, data] }));
    return data;
  },

  deleteLabel: async (labelId) => {
    await supabase.from('whatsapp_chat_labels').delete().eq('id', labelId);
    set((s) => ({ orgLabels: s.orgLabels.filter((l) => l.id !== labelId) }));
  },

  assignLabel: async (chatJid, labelId, organizationId, chatConfigId) => {
    const { error } = await supabase
      .from('whatsapp_chat_label_assignments')
      .upsert({ organization_id: organizationId, chat_jid: chatJid, label_id: labelId }, { onConflict: 'organization_id,chat_jid,label_id' });
    if (error) return { status: 'error', message: error.message };
    const label = get().orgLabels.find((l) => l.id === labelId);
    if (!label) return { status: 'crm_only' };
    const newMap = new Map(get().chatLabels);
    const existing = newMap.get(chatJid) || [];
    if (!existing.find((l) => l.id === labelId)) {
      newMap.set(chatJid, [...existing, label]);
    }
    set({ chatLabels: newMap });
    return syncChatLabelToWhatsApp(label, chatJid, true, chatConfigId);
  },

  removeLabel: async (chatJid, labelId, organizationId, chatConfigId) => {
    const { error } = await supabase
      .from('whatsapp_chat_label_assignments')
      .delete()
      .eq('organization_id', organizationId)
      .eq('chat_jid', chatJid)
      .eq('label_id', labelId);
    if (error) return { status: 'error', message: error.message };
    const newMap = new Map(get().chatLabels);
    const existing = newMap.get(chatJid) || [];
    newMap.set(chatJid, existing.filter((l) => l.id !== labelId));
    set({ chatLabels: newMap });
    const label = get().orgLabels.find((l) => l.id === labelId) ?? existing.find((l) => l.id === labelId);
    return syncChatLabelToWhatsApp(label, chatJid, false, chatConfigId);
  },

  // ============ META ============

  fetchChatMeta: async (chatJids, organizationId) => {
    if (chatJids.length === 0) return;
    const leggi = (columns: string) => supabase
      .from('whatsapp_chat_meta')
      .select(columns)
      .eq('organization_id', organizationId)
      .in('chat_jid', chatJids);
    // Una colonna inesistente farebbe fallire tutta la select (niente stato ne' operatore):
    // se la migration 20260926a non e' ancora applicata si ripiega sulle colonne di base.
    let { data, error } = await leggi(get().listPrefsAvailable ? META_PREFS_COLUMNS : META_BASE_COLUMNS);
    if (error && error.code === '42703' && get().listPrefsAvailable) {
      set({ listPrefsAvailable: false });
      ({ data, error } = await leggi(META_BASE_COLUMNS));
    }
    if (error || !data) return;
    const newMap = new Map(get().chatMeta);
    for (const row of data as unknown as ChatMeta[]) {
      newMap.set(row.chat_jid, row);
    }
    set({ chatMeta: newMap });
  },

  reopenIfClosed: (chatJid) => {
    const current = get().chatMeta.get(chatJid);
    if (!current || current.conversation_status !== 'chiusa') return;
    const newMap = new Map(get().chatMeta);
    newMap.set(chatJid, { ...current, conversation_status: 'da_gestire' });
    set({ chatMeta: newMap });
  },

  upsertMeta: async (chatJid, changes, organizationId) => {
    const existing = get().chatMeta.get(chatJid);
    const newMeta: ChatMeta = {
      ...existing,
      chat_jid: chatJid,
      conversation_status: existing?.conversation_status || 'da_gestire',
      assigned_operator_id: existing?.assigned_operator_id || null,
      assigned_at: existing?.assigned_at || null,
      ...changes,
    };

    // Optimistic update
    const newMap = new Map(get().chatMeta);
    newMap.set(chatJid, newMeta);
    set({ chatMeta: newMap });

    const { error } = await supabase
      .from('whatsapp_chat_meta')
      .upsert(
        { organization_id: organizationId, chat_jid: chatJid, ...changes, updated_at: new Date().toISOString() },
        { onConflict: 'organization_id,chat_jid' }
      );
    if (error) console.error('[ChatMeta] upsert error:', error);
  },

  setChatListPrefs: async (chatJid, changes, organizationId) => {
    const previous = get().chatMeta.get(chatJid);
    const optimistic: ChatMeta = {
      conversation_status: 'da_gestire',
      assigned_operator_id: null,
      assigned_at: null,
      ...previous,
      ...changes,
      chat_jid: chatJid,
    };
    const newMap = new Map(get().chatMeta);
    newMap.set(chatJid, optimistic);
    set({ chatMeta: newMap });

    const { error } = await supabase
      .from('whatsapp_chat_meta')
      .upsert(
        { organization_id: organizationId, chat_jid: chatJid, ...changes, updated_at: new Date().toISOString() },
        { onConflict: 'organization_id,chat_jid' }
      );
    if (!error) return true;
    console.error('[ChatMeta] setChatListPrefs error:', error);
    if (error.code === '42703' || error.code === 'PGRST204') set({ listPrefsAvailable: false });
    const rollback = new Map(get().chatMeta);
    if (previous) rollback.set(chatJid, previous);
    else rollback.delete(chatJid);
    set({ chatMeta: rollback });
    return false;
  },

  // ============ NOTE ============

  fetchNotes: async (chatJid, organizationId) => {
    set({ notesLoading: true });
    const { data, error } = await supabase
      .from('whatsapp_chat_notes')
      .select('id, chat_jid, content, created_by, created_at, updated_at')
      .eq('organization_id', organizationId)
      .eq('chat_jid', chatJid)
      .order('created_at', { ascending: true });
    set({ notesLoading: false });
    if (error || !data) return;

    // Fetch author names
    const authorIds = [...new Set(data.map((n) => n.created_by))];
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, full_name')
      .in('id', authorIds);
    const nameMap = new Map((profiles || []).map((p) => [p.id, p.full_name]));

    const notes: ChatNote[] = data.map((n) => ({ ...n, author_name: nameMap.get(n.created_by) || 'Utente' }));
    const newMap = new Map(get().chatNotes);
    newMap.set(chatJid, notes);
    set({ chatNotes: newMap });
  },

  addNote: async (chatJid, content, organizationId) => {
    const { data: userData } = await supabase.auth.getUser();
    if (!userData.user) return;
    const { data, error } = await supabase
      .from('whatsapp_chat_notes')
      .insert({ organization_id: organizationId, chat_jid: chatJid, content, created_by: userData.user.id })
      .select()
      .single();
    if (error || !data) return;
    const newMap = new Map(get().chatNotes);
    const existing = newMap.get(chatJid) || [];
    newMap.set(chatJid, [...existing, { ...data, author_name: 'Tu' }]);
    set({ chatNotes: newMap });
  },

  deleteNote: async (noteId, chatJid) => {
    await supabase.from('whatsapp_chat_notes').delete().eq('id', noteId);
    const newMap = new Map(get().chatNotes);
    const existing = newMap.get(chatJid) || [];
    newMap.set(chatJid, existing.filter((n) => n.id !== noteId));
    set({ chatNotes: newMap });
  },

  // ============ TEMPLATE ============

  fetchTemplates: async (organizationId) => {
    set({ templatesLoading: true });
    const { data, error } = await supabase
      .from('whatsapp_message_templates')
      .select('*')
      .eq('organization_id', organizationId)
      .order('name');
    set({ templatesLoading: false });
    if (!error && data) set({ templates: data });
  },

  createTemplate: async (organizationId, name, content, category) => {
    const { data, error } = await supabase
      .from('whatsapp_message_templates')
      .insert({ organization_id: organizationId, name, content, category: category || null })
      .select()
      .single();
    if (!error && data) set((s) => ({ templates: [...s.templates, data] }));
  },

  deleteTemplate: async (templateId) => {
    await supabase.from('whatsapp_message_templates').delete().eq('id', templateId);
    set((s) => ({ templates: s.templates.filter((t) => t.id !== templateId) }));
  },

  // ============ TEAM ============

  fetchOrgMembers: async (organizationId) => {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, full_name, email, profile_image_url')
      .eq('organization_id', organizationId)
      .eq('active', true)
      .order('full_name');
    if (!error && data) set({ orgMembers: data });
  },

  // ============ FILTRI ============

  setFilters: (partial) => set((s) => ({ filters: { ...s.filters, ...partial } })),
  resetFilters: () => set({ filters: { ...DEFAULT_FILTERS } }),
}));
